import React from 'react'
import { useForm } from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'

import { useEventCapture } from '@surglogs-app/analytics'
import {
  MfaAbortTimerText,
  MfaAuthenticatorAppVerify,
} from '@surglogs-app/auth-mfa'
import { useHandler } from '@surglogs-app/hooks'
import { Box, Button, Divider } from '@surglogs-app/ui'
import { ComponentLoader } from '@surglogs-app/ui.loader'

import { useMfaSetupVerifySubmitHandler } from '../hooks/useMfaSetupVerifySubmitHandler'
import { mfaCodeFormSchema } from '../validators/mfaCodeFormSchema'

interface MfaAuthenticatorVerifyFormProps {
  setActiveView: (view: 'mfa_step1' | 'mfa_step2') => void
  onClose: () => void
  onMfaSetupCompleted: () => Promise<void>
  context: 'account' | 'settings'
  transactionId: string
  t: TFunction<any>
  isLastMinute: boolean
  timeLeft: number | null
}

export const MfaAuthenticatorVerifyForm = ({
  transactionId,
  setActiveView,
  onClose,
  onMfaSetupCompleted,
  t,
  context,
  isLastMinute = false,
  timeLeft = null,
}: MfaAuthenticatorVerifyFormProps) => {
  const { captureEvent } = useEventCapture()

  const {
    handleSubmit,
    register,
    formState: { errors, isDirty },
    control,
    setError,
  } = useForm<{ mfaCode: string }>({
    mode: 'onBlur',
    resolver: yupResolver(mfaCodeFormSchema),
  })

  const { isPending, onSubmit } = useMfaSetupVerifySubmitHandler({
    transactionId,
    onMfaSetupCompleted,
    onClose,
    setError,
    t,
  })

  const onForgotScanClick = useHandler(() => {
    captureEvent('mfa_code_scan_forgot_clicked', { context })
    setActiveView('mfa_step1')
  })

  const onCancelHandler = useHandler(() => {
    captureEvent('mfa_setup_code_step_canceled', { context })
    onClose()
  })

  return (
    <form {...{ onSubmit: handleSubmit(onSubmit), noValidate: true }}>
      <Box
        {...{
          px: 'xl',
          pt: 'md',
          pb: 'sm',
          width: '100%',
          mih: '200px',
          ac: 'center',
        }}>
        {isPending ? (
          <ComponentLoader />
        ) : (
          <MfaAuthenticatorAppVerify
            {...{ onForgotScanClick, t, register, errors, control }}
          />
        )}
      </Box>

      <MfaAbortTimerText {...{ mode: 'setup', isLastMinute, timeLeft, t }} />

      <Divider {...{ my: 'abs.30', mx: 'abs.0' }} />

      <Box {...{ px: 'xl', pb: 'xl', width: '100%' }}>
        <Button
          {...{
            variant: 'success',
            type: 'submit',
            disabled: isPending || !isDirty,
          }}>
          {t('mfaSetup.step2.enableMFA')}
        </Button>
        <Button
          {...{ variant: 'secondary', ml: 'md', onClick: onCancelHandler }}>
          {t('mfaSetup.step1.cancel')}
        </Button>
      </Box>
    </form>
  )
}
